/**
 * Static Translation Service
 *
 * Synchronous lookup from pre-built JSON locale files.
 * Zero runtime cost, zero network calls, zero flicker.
 *
 * Locale files are generated by `npm run i18n:generate` and committed to repo.
 * CI/CD auto-regenerates when en.json changes.
 *
 * @security No sensitive data should be passed through translation
 */

import en from './locales/en.json';
import de from './locales/de.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import nl from './locales/nl.json';
import pt from './locales/pt.json';
import sv from './locales/sv.json';
import th from './locales/th.json';
import zh from './locales/zh.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', rtl: false },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', rtl: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', rtl: false },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', rtl: false },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', rtl: false },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', rtl: false },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳', rtl: false },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

const locales: Record<string, Record<string, string>> = {
  en, de, es, fr, it, ja, nl, pt, sv, th, zh,
};

/**
 * Translate a string synchronously from the pre-built locale files.
 * Falls back to the original text if no translation exists.
 */
export function translateText(
  text: string,
  targetLang: LanguageCode,
  _sourceLang: LanguageCode = 'en'
): string {
  if (targetLang === 'en') return text;
  return locales[targetLang]?.[text] ?? text;
}

/**
 * Batch translate — synchronous version for backward compatibility.
 * Used by CRM inbox for auto-translating inbound messages.
 */
export function translateBatch(
  texts: string[],
  targetLang: LanguageCode,
  _sourceLang: LanguageCode | 'auto' = 'en'
): string[] {
  if (targetLang === 'en') return texts;
  const locale = locales[targetLang] ?? {};
  return texts.map(t => locale[t] ?? t);
}

export function clearTranslationCache(): void {
  // No-op — static files don't need cache clearing
}

export function getLanguageInfo(code: LanguageCode) {
  return SUPPORTED_LANGUAGES.find(l => l.code === code);
}

export function isRTL(code: LanguageCode): boolean {
  return getLanguageInfo(code)?.rtl ?? false;
}

/**
 * Convert a CRM locale code (e.g. 'us-en', 'de-de') or plain language code ('en', 'de')
 * to a valid LanguageCode. Falls back to 'en' for unrecognized codes.
 */
export function toLanguageCode(crmLocale: string | null | undefined): LanguageCode {
  if (!crmLocale) return 'en';

  if (SUPPORTED_LANGUAGES.some(l => l.code === crmLocale)) {
    return crmLocale as LanguageCode;
  }

  const langPart = crmLocale.split('-')[1] || crmLocale;
  const match = SUPPORTED_LANGUAGES.find(l => l.code === langPart);
  return match ? match.code : 'en';
}
